import type { Comment, ReactionType } from '@/shared/types/comment';
import type { PersonId } from '@/shared/types/profile';
import type { Person } from '../types';
import { Paths } from '../../constants';
import { apiInvoke } from '@/store/helpers/load';
import { makePayloadRequestInit } from '@/lib/api';
import type { APIParams } from '@/store/helpers/types';

export interface CommentsAccessResponse {
  allowed: boolean;
}

export async function getCommentsAccess({
  api,
  auth,
  subjectId,
}: { subjectId: PersonId } & APIParams) {
  const path = `${Paths.comments}/access?subject_id=${encodeURIComponent(subjectId)}`;
  const res = await apiInvoke<CommentsAccessResponse>(api, path, auth, {
    method: 'GET',
  });

  return res;
}

export async function loadComments({
  api,
  auth,
  subject,
}: { subject: Person } & APIParams) {
  const path = `${Paths.comments}/comments`;
  const payload: LoadCommentsRequest = {
    subject_id: subject.id,
  };
  const res = await apiInvoke<LoadCommentsResponse>(
    api,
    path,
    auth,
    makePayloadRequestInit(payload, 'POST'),
  );

  return res;
}

interface LoadCommentsRequest {
  subject_id: PersonId;
}

type LoadCommentsResponse = Comment[];

export async function postComment({
  api,
  auth,
  comment,
}: { comment: Comment } & APIParams) {
  const path = `${Paths.comments}/posts`;
  const payload: PostCommentRequest = comment.data;
  const res = await apiInvoke<PostCommentResponse>(
    api,
    path,
    auth,
    makePayloadRequestInit(payload, 'POST'),
  );

  return res;
}

export async function updateComment({
  api,
  auth,
  comment,
}: {
  comment: { id: string; data: Partial<Comment['data']> };
} & APIParams) {
  const path = `${Paths.comments}/posts/${comment.id}`;
  const payload: UpdateCommentRequest = comment.data;
  const res = await apiInvoke<UpdateCommentResponse>(
    api,
    path,
    auth,
    makePayloadRequestInit(payload, 'PATCH'),
  );

  return res;
}

export async function reactToComment({
  api,
  auth,
  id,
  userId,
  type,
  action,
}: {
  id: string;
  userId: string;
  type: ReactionType;
  action?: 'toggle' | 'add' | 'remove';
} & APIParams) {
  const path = `${Paths.comments}/posts/${id}/reactions`;
  const payload = { userId, type, action };
  const res = await apiInvoke<ReactToCommentResponse>(
    api,
    path,
    auth,
    makePayloadRequestInit(payload, 'POST'),
  );

  return res;
}

type ReactToCommentResponse = Comment;

type PostCommentRequest = Omit<
  Comment['data'],
  'created_at' | 'created_by' | 'updated_at'
>;

type PostCommentResponse = Comment;

type UpdateCommentRequest = Partial<PostCommentRequest>;

type UpdateCommentResponse = Comment;
